import type {AxiosRequestConfig} from 'axios'
import axios from 'axios'
import {HTTPMethod, ContentType} from '../utils/constants/globalConstants'
import {attachInterceptors} from './interceptors'
import {API_BASE_URL} from '@/config/envConfig'

/**
 * Creates an Axios instance with proper configuration based on the environment
 */
const createApiClient = (baseUrlKey = 'API_BASE_URL') => {
  // Get the base URL from environment variables
  const baseURL = import.meta.env[baseUrlKey] ?? API_BASE_URL
  // Create the Axios instance with standard configuration
  const client = axios.create({
    baseURL,
    timeout: 60000, // 60 seconds timeout
    headers: {
      'Content-Type': ContentType.APPLICATION_JSON,
    },
  })

  // Attach all interceptors with default configuration
  return attachInterceptors(client)
}

// Create the default API client instance
const axiosInstance = createApiClient()

/**
 * Processes a path to handle different base URLs and replace parameters
 *
 * @param path The API path with optional placeholders
 * @param params Any parameters to replace in the path
 * @returns Processed URL and baseURL
 */
const pathAdapter = (path: string, params?: Record<string, string>) => {
  const baseURLKey = 'API_BASE_URL'
  let url = path

  // Replace path parameters if any
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`:${key}`, value)
    })
  }

  return {url, baseURLKey}
}

// Type for request options
interface RequestOptions {
  params?: Record<string, any>
  data?: any
  headers?: Record<string, string>
  signal?: AbortSignal
  timeout?: number
  onUploadProgress?: (progressEvent: any) => void
  pathParams?: Record<string, string> // For replacing path parameters like :id
  responseType?: 'json' | 'blob' | 'arraybuffer' | 'text' // Allow specifying response type for downloads
}

/**
 * Main request function for API calls
 */
export const request = async (
  path: string,
  options: RequestOptions = {},
  method = HTTPMethod.GET,
  contentType = ContentType.APPLICATION_JSON,
) => {
  // Extract options
  const {pathParams, ...restOptions} = options

  // Process the path using the adapter
  const {url, baseURLKey} = pathAdapter(path, pathParams)

  // Create a client with the appropriate base URL
  const apiClient = createApiClient(baseURLKey)

  const config: AxiosRequestConfig = {
    url,
    method,
    headers: {
      'Content-Type': contentType,
      ...restOptions.headers,
    },
    signal: restOptions.signal,
    onUploadProgress: restOptions.onUploadProgress,
    responseType: restOptions.responseType,
  }

  // Add query params for GET requests, or request body for other methods
  if (method === HTTPMethod.GET) {
    config.params = restOptions.params ?? restOptions.data
  } else {
    config.data = restOptions.data
    // For non-GET requests, also add params as query parameters if they exist
    if (restOptions.params) {
      config.params = restOptions.params
    }
  }

  return await apiClient.request(config)
}

/**
 * Helper methods for common HTTP methods
 */
export const get = (path: string, options?: RequestOptions) => request(path, options, HTTPMethod.GET)

export const post = (path: string, options?: RequestOptions) => request(path, options, HTTPMethod.POST)

export const put = (path: string, options?: RequestOptions) => request(path, options, HTTPMethod.PUT)

export const del = (path: string, options?: RequestOptions) => request(path, options, HTTPMethod.DELETE)

export const patch = (path: string, options?: RequestOptions) => request(path, options, HTTPMethod.PATCH)

// Export default axios instance and createApiClient for special cases
export {createApiClient}
export default axiosInstance
